import { isValidLegendSpecSize, TimeSeriesChartSpec, timeSeriesChartSpecSchema } from '@/core';
import { createVisualization, mapPropsToSpec, mapSpecToProps } from '../../../plugin-system';
import { TimeSeriesChartIcon } from './TimeSeriesChartIcon';
import { TimeSeriesChartPanel } from './TimeSeriesChartPanel';
import { TimeSeriesChartPanelContent } from './TimeSeriesChartPanelContent';
import {
  TimeSeriesChartOptionsEditorAdvancedSettings,
  TimeSeriesChartOptionsEditorGeneralSettings,
} from './TimeSeriesChartPanelOptionsEditor';
import { createInitialTimeSeriesChartSpec, TIME_SERIES_CHART_PROP_TO_SPEC_MAPPING } from './time-series-chart-model';

export const TimeSeriesChart = createVisualization(TimeSeriesChartPanel, {
  dashboardConfig: {
    kind: 'TimeSeriesChart',
    mapSpecToProps: (spec) => mapSpecToProps(spec, TIME_SERIES_CHART_PROP_TO_SPEC_MAPPING),
    mapPropsToSpec: (props) => {
      const initSpec: TimeSeriesChartSpec = mapPropsToSpec(props, TIME_SERIES_CHART_PROP_TO_SPEC_MAPPING);

      if (initSpec.legend) {
        const { size, ...otherLegend } = initSpec.legend;

        // Remove legend size values that are not supported on dashboards.
        if (size && !isValidLegendSpecSize(size)) {
          initSpec.legend = otherLegend;
        }
      }

      return initSpec;
    },
    createInitialOptions: createInitialTimeSeriesChartSpec,
    panelOptionsEditorComponents: [
      { label: 'Settings', content: TimeSeriesChartOptionsEditorGeneralSettings },
      { label: 'Advanced', content: TimeSeriesChartOptionsEditorAdvancedSettings },
    ],
    contextLinkConfig: {
      scopeName: 'Series',
      scopeVariables: ['name', 'labels.<label_name>', 'timestamp', 'value'],
    },
    enableCompareMode: true,
    schema: timeSeriesChartSpecSchema,
    supportedDataType: 'TimeSeries',
    icon: TimeSeriesChartIcon,
  },
  childComponents: {
    /**
     * Component used for rendering the content of the TimeSeriesChart panel. This is a render-focused component that
     * assumes data, calculations, and other processing have already been completed.
     */
    PanelContent: TimeSeriesChartPanelContent,
  },
});

export type TimeSeriesChartVizProps = React.ComponentProps<typeof TimeSeriesChart.Viz>;
export type TimeSeriesChartVizBaseProps = React.ComponentProps<typeof TimeSeriesChart.VizBase>;
